import { FlexColumnSpaced, FlexRowSmallSpaced, Padded, SpaceBetweenRow } from '@fox/layout';
import React, { ReactElement } from 'react';
import { ThemeContext } from 'styled-components';
import { Icon, IconName } from '../icon';
import { IconButton } from '../icon-button';
import { LabeledCheckbox } from '../labeled-checkbox';
import { AccordionDetails, AccordionSummary, MuiAccordion } from '../mui-accordion';
import { Popover } from '../popover';
import { TextField } from '../text-field';
import { Typography } from '../typography';

const FIELD_INPUT_WIDTH = '25rem';

interface AccordionSectionProps {
	defaultExpanded?: boolean;
	summaryTitle: string;
	details: React.ReactNode;
	limitedWidth?: boolean;
	/**
	 * If this is set to true, the title of the accordion will become editable by the user.
	 */
	editing?: boolean;
	/**
	 * If true, disables the ability to edit the title. Other aspects are still editable if {@link AccordionSectionProps#editing} is true
	 */
	disableTitleEditing?: boolean;
	/**
	 * A handler for when the title is updated; used in conjunction with the 'editing' prop.
	 */
	onUpdateTitle?: (title: string) => void;
	/**
	 * Sets the label for the textfield in edit mode.
	 */
	fieldLabel?: string;
	/**
	 * A component that can be passed in for use in the accordion summary.
	 */
	summaryEditComponent?: ReactElement;
	/**
	 * Force the edit summary component to appear in readonly mode
	 */
	forceSummaryEditComponent?: boolean;

	summaryTitleIcon?: IconName | null | undefined;
}

const SectionTitle = ({ summaryTitle, summaryTitleIcon }: Pick<AccordionSectionProps, 'summaryTitle' | 'summaryTitleIcon'>) => {
	return (
		<FlexRowSmallSpaced style={{ alignItems: 'center' }}>
			<Typography variant="h5">{summaryTitle}</Typography>
			{summaryTitleIcon ? <Icon iconName={summaryTitleIcon} /> : null}
		</FlexRowSmallSpaced>
	);
};

export const AccordionSection = ({
	defaultExpanded = false,
	summaryTitle,
	details,
	limitedWidth,
	editing = false,
	disableTitleEditing = false,
	onUpdateTitle,
	fieldLabel,
	summaryEditComponent,
	forceSummaryEditComponent,
	summaryTitleIcon
}: AccordionSectionProps) => {
	const theme = React.useContext(ThemeContext);

	return (
		<MuiAccordion
			elevation={0}
			disableGutters={true}
			defaultExpanded={defaultExpanded}
			square={true}
			sx={{
				border: 'none',
				'&:before': {
					display: 'none'
				}
			}}
			padLeft
		>
			<AccordionSummary
				disableRipple={true}
				style={{ backgroundColor: theme?.colors?.grey?.[100] }}
				expandIcon={<Icon iconName="KeyboardArrowDown" />}
			>
				{!editing ? (
					<SpaceBetweenRow>
						<SectionTitle summaryTitle={summaryTitle} summaryTitleIcon={summaryTitleIcon} />
						{forceSummaryEditComponent ? <div onClick={e => e.stopPropagation()}>{summaryEditComponent}</div> : null}
					</SpaceBetweenRow>
				) : (
					<SpaceBetweenRow onClick={e => e.stopPropagation()}>
						{disableTitleEditing ? (
							<SectionTitle summaryTitle={summaryTitle} summaryTitleIcon={summaryTitleIcon} />
						) : (
							<TextField
								style={{ flex: 1 }}
								defaultValue={summaryTitle}
								onChange={e => onUpdateTitle?.(e.target.value)}
								label={fieldLabel}
							/>
						)}
						{summaryEditComponent ?? <></>}
					</SpaceBetweenRow>
				)}
			</AccordionSummary>
			<AccordionDetails>
				<FlexColumnSpaced style={limitedWidth ? { width: FIELD_INPUT_WIDTH } : {}}>{details}</FlexColumnSpaced>
			</AccordionDetails>
		</MuiAccordion>
	);
};

export const SectionSettingsPopover = ({
	expand,
	onExpand,
	children
}: {
	expand?: boolean;
	onExpand: (expand: boolean) => void;
	children?: React.ReactNode;
}) => {
	return (
		<Popover overlay={<SectionSettingsOverlay expand={expand ?? false} onExpand={onExpand} children={children} />}>
			<IconButton iconName="Settings" />
		</Popover>
	);
};

export const SectionSettingsOverlay = ({
	expand,
	onExpand,
	children
}: {
	expand: boolean;
	onExpand: (expand: boolean) => void;
	children?: React.ReactNode;
}) => {
	return (
		<Padded>
			<FlexColumnSpaced>
				<Typography variant="h5" component="h1">
					Update Section
				</Typography>
				<LabeledCheckbox
					label="Expand section by default"
					onChange={(_, checked) => onExpand(checked)}
					checkboxProps={{ defaultChecked: expand }}
				/>
				{children}
			</FlexColumnSpaced>
		</Padded>
	);
};
